<template>
    <div id="webhook-events-page">
        <loader v-if="loading" :addBackground="true"></loader>

        <div class="container-fluid">
            <div class="box padding-large page-content">
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-sm">
                    <div>
                        <h1 class="mb-1">{{ t('settings-connections-webhooks-title') }}</h1>
                        <div class="text-muted" v-if="sender">
                            <strong>{{ sender.alias }}</strong>
                            <span v-if="sender.sender_name"> · {{ sender.sender_name }}</span>
                            <span v-if="sender.channel"> · {{ sender.channel }}</span>
                        </div>
                    </div>
                    <div class="d-flex align-items-center gap-sm">
                        <button class="btn btn-outline" type="button" @click="goBack">
                            {{ t('settings-connections-webhooks-back') }}
                        </button>
                    </div>
                </div>

                <hr class="my-4" />

                <div class="box box-secondary bg-black-50 p-3">
                    <div class="d-flex align-items-center justify-content-between flex-wrap gap-sm">
                        <div class="d-flex align-items-center gap-sm">
                            <span class="badge" :class="statusBadgeClass">
                                {{ statusLabel }}
                            </span>
                            <div class="text-sm text-muted">
                                {{ t('settings-connections-webhooks-status_24h_summary') }}
                            </div>
                        </div>
                        <div v-if="sender?.webhook_last_received_at" class="text-sm text-muted">
                            {{ t('settings-connections-webhooks-last_event', [sender.webhook_last_received_at]) }}
                        </div>
                    </div>

                    <div class="mt-3 text-sm">
                        <p class="mb-2">
                            <span v-html="t('settings-connections-webhooks-description_line_1')"></span>
                        </p>
                        <p class="mb-0">
                            {{ t('settings-connections-webhooks-description_line_2') }}
                        </p>
                    </div>

                    <div v-if="sender?.webhook_supported === false" class="mt-3 text-sm text-muted">
                        {{ t('settings-connections-common-sender_no_webhooks') }}
                    </div>

                    <div v-else-if="sender?.by_kevi" class="mt-3 text-sm text-muted">
                        {{ t('settings-connections-webhooks-managed_by_kevi') }}
                    </div>

                    <div v-else-if="sender?.webhook_url" class="mt-3">
                        <div class="text-sm text-muted mb-1">{{ t('settings-connections-webhooks-webhook_url') }}</div>
                        <div class="d-flex align-items-center gap-sm flex-wrap">
                            <code style="word-break: break-all;">{{ sender.webhook_url }}</code>
                            <button class="btn btn-small btn-default" type="button" @click="copyWebhookUrl">
                                {{ t('settings-connections-webhooks-copy') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="mt-4">
                    <h2 class="mb-3">{{ t('settings-connections-webhooks-received_events') }}</h2>

                    <div v-if="gridLoaded && !events.length" class="text-muted">
                        {{ t('settings-connections-webhooks-no_events') }}
                    </div>

                    <grid
                        :source="gridSource"
                        name="webhook_events"
                        :show-search="false"
                        :filters="gridFilters"
                        display-items-per-page
                        @loaded="loadedGrid"
                    >
                        <template v-slot:rows="grid">
                            <thead>
                                <tr>
                                    <grid-th :grid="grid">{{ t('settings-connections-webhooks-received_at') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('settings-connections-webhooks-event') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('settings-connections-webhooks-contact') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('settings-connections-webhooks-client') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('settings-connections-webhooks-provider_id') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('settings-connections-webhooks-status') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('settings-connections-webhooks-error') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('settings-connections-webhooks-data') }}</grid-th>
                                </tr>
                            </thead>
                            <tbody v-for="e in grid.data" :key="e.id">
                                <tr>
                                    <td style="min-width: 170px;">{{ e.received_at }}</td>
                                    <td style="min-width: 120px;">{{ e.event_type || '—' }}</td>
                                    <td style="min-width: 200px;">
                                        <div class="d-flex align-items-center flex-wrap gap-sm">
                                            <span>{{ e.contact || '—' }}</span>
                                            <NotificationRecipientChip
                                                :address="e.recipient_address"
                                                :method="e.notification_method"
                                            />
                                        </div>
                                    </td>
                                    <td style="min-width: 220px; max-width: 260px;">
                                        <span class="text-muted" v-if="!e.clients_id">—</span>
                                        <router-link v-else :to="`/client/${e.clients_id}`">
                                            {{ e.client_name || e.client_email || e.clients_id }}
                                        </router-link>
                                    </td>
                                    <td style="min-width: 240px;">{{ e.provider_message_id || '—' }}</td>
                                    <td style="min-width: 120px;">
                                        <span class="badge" :class="isSuccess(e) ? 'badge-success' : 'badge-danger'">
                                            {{ isSuccess(e) ? t('settings-connections-webhooks-processed') : t('settings-connections-webhooks-failed') }}
                                        </span>
                                    </td>
                                    <td style="min-width: 260px; max-width: 420px;">
                                        <span class="text-muted" v-if="!e.processed_error">—</span>
                                        <span v-else>{{ e.processed_error }}</span>
                                    </td>
                                    <td style="min-width: 120px;">
                                        <span class="text-muted" v-if="!hasDetails(e)">—</span>
                                        <KvButton
                                            v-else
                                            variant="outlined"
                                            size="small"
                                            type="button"
                                            @click.stop="openDetails(e)"
                                        >
                                            {{ t('settings-connections-webhooks-view_data') }}
                                        </KvButton>
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </grid>
                </div>

                <modal id="webhook-event-details" ref="detailsModal" size="lg">
                    <template slot="title">
                        {{ t('settings-connections-webhooks-details_title') }}
                    </template>
                    <template slot="subtitle">
                        <span v-if="selectedEvent">
                            {{ selectedEvent.received_at || '—' }}
                            <span v-if="selectedEvent.event_type"> · {{ selectedEvent.event_type }}</span>
                            <span v-if="selectedEvent.contact"> · {{ selectedEvent.contact }}</span>
                        </span>
                    </template>
                    <template slot="body">
                        <div v-if="selectedEvent">
                            <div class="mb-3">
                                <div class="text-xs text-muted mb-2">{{ t('settings-connections-webhooks-summary') }}</div>
                                <div class="d-flex align-items-center flex-wrap gap-sm">
                                    <div>
                                        <strong>{{ t('settings-connections-webhooks-client') }}:</strong>
                                        <span class="text-muted" v-if="!selectedEvent.clients_id"> —</span>
                                        <router-link v-else :to="`/client/${selectedEvent.clients_id}`">
                                            {{ selectedEvent.client_name || selectedEvent.client_email || selectedEvent.clients_id }}
                                        </router-link>
                                    </div>
                                    <div class="text-muted">|</div>
                                    <div><strong>{{ t('settings-connections-webhooks-received') }}:</strong> {{ selectedEvent.received_at || '—' }}</div>
                                    <div class="text-muted">|</div>
                                    <div><strong>{{ t('settings-connections-webhooks-event') }}:</strong> {{ selectedEvent.event_type || '—' }}</div>
                                    <div class="text-muted">|</div>
                                    <div><strong>{{ t('settings-connections-webhooks-contact') }}:</strong> {{ selectedEvent.contact || '—' }}</div>
                                    <NotificationRecipientChip
                                        :address="selectedEvent.recipient_address"
                                        :method="selectedEvent.notification_method"
                                    />
                                </div>

                                <div class="mt-2 d-flex align-items-center flex-wrap gap-sm">
                                    <div><strong>{{ t('settings-connections-webhooks-provider_id') }}:</strong> {{ selectedEvent.provider_message_id || '—' }}</div>
                                    <div class="text-muted">|</div>
                                    <div><strong>{{ t('settings-connections-webhooks-provider_event') }}:</strong> {{ selectedEvent.provider_event_id || '—' }}</div>
                                    <div class="text-muted">|</div>
                                    <div>
                                        <strong>{{ t('settings-connections-webhooks-status') }}:</strong>
                                        <span :class="isSuccess(selectedEvent) ? 'text-success' : 'text-danger'">
                                            {{ isSuccess(selectedEvent) ? t('settings-connections-webhooks-processed') : t('settings-connections-webhooks-failed') }}
                                        </span>
                                    </div>
                                </div>

                                <div class="mt-2" v-if="selectedEvent.processed_error">
                                    <strong>{{ t('settings-connections-webhooks-error') }}:</strong> {{ selectedEvent.processed_error }}
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="text-xs text-muted mb-1">{{ t('settings-connections-webhooks-payload_json') }}</div>
                                <pre class="payload-pre">{{ formatJson(selectedEvent.payload) }}</pre>
                            </div>

                            <div>
                                <div class="text-xs text-muted mb-1">{{ t('settings-connections-webhooks-headers_json') }}</div>
                                <pre class="payload-pre">{{ formatJson(selectedEvent.headers) }}</pre>
                            </div>
                        </div>
                        <div v-else class="text-muted">—</div>
                    </template>
                    <template slot="footer" slot-scope="{ toggle }">
                        <KvButton variant="text" @click="toggle(false)">{{ t('settings-connections-webhooks-close') }}</KvButton>
                    </template>
                </modal>
            </div>
        </div>
    </div>
</template>

<script>
import grid from "../../../components/grid/grid.vue";
import gridTh from "../../../components/grid/grid-th.vue";
import KvButton from "@/components/form/kv-button.vue";
import Modal from "../../../components/modal/modal.vue";
import NotificationRecipientChip from "@/components/ui/notification-recipient-chip.vue";

export default {
    name: "WebhookEventsPage",
    components: { grid, gridTh, KvButton, modal: Modal, NotificationRecipientChip },
    data() {
        return {
            loading: false,
            sender: null,
            events: [],
            gridLoaded: false,
            gridFilters: {},
            selectedEvent: null,
        };
    },
    computed: {
        gridSource() {
            const id = this.$route.params.id;
            return `senders/${id}/webhook-events`;
        },
        statusLabel() {
            if (this.sender?.webhook_supported === false || this.sender?.webhook_status === "unavailable") {
                return this.$_('settings-connections-common-not_supported');
            }
            return this.sender?.webhook_status === "success" ? this.$_('settings-connections-common-active') : this.$_('settings-connections-common-not_configured');
        },
        statusBadgeClass() {
            if (this.sender?.webhook_supported === false || this.sender?.webhook_status === "unavailable") {
                return "badge-secondary";
            }
            return this.sender?.webhook_status === "success" ? "badge-success" : "badge-warning";
        },
    },
    methods: {
        isSuccess(e) {
            return e?.processed_success === 1 || e?.processed_success === "1" || e?.processed_success === true;
        },
        hasDetails(e) {
            return !!(e?.payload || e?.headers || e?.raw_body);
        },
        openDetails(e) {
            this.selectedEvent = e || null;
            this.$nextTick(() => {
                this.$refs.detailsModal?.open?.();
            });
        },
        formatJson(value) {
            if (!value) return "—";
            try {
                if (typeof value === "string") {
                    const parsed = JSON.parse(value);
                    return JSON.stringify(parsed, null, 2);
                }
                return JSON.stringify(value, null, 2);
            } catch (e) {
                return String(value);
            }
        },
        goBack() {
            this.$router.push("/connections");
        },
        loadedGrid(payload) {
            this.gridLoaded = true;
            this.events = payload?.data || [];
            this.sender = payload?.response?.sender || this.sender;
        },
        async copyWebhookUrl() {
            const url = this.sender?.webhook_url;
            if (!url) return;
            try {
                await navigator.clipboard.writeText(url);
                this.$store.dispatch("notification/success", this.$_('settings-connections-common-copied_success'));
            } catch (e) {
                // Fallback
                const textArea = document.createElement("textarea");
                textArea.value = url;
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.select();
                try {
                    document.execCommand("copy");
                    this.$store.dispatch("notification/success", this.$_('settings-connections-common-copied_success'));
                } catch (err) {
                    this.$store.dispatch("notification/error", this.$_('settings-connections-common-copy_error'));
                }
                document.body.removeChild(textArea);
            }
        },
    },
    watch: {
        "$route.params.id": function () {
            this.sender = null;
            this.events = [];
            this.gridLoaded = false;
            this.selectedEvent = null;
            this.$nextTick(() => {
                this.$refs.detailsModal?.close?.();
            });
        },
    },
};
</script>

<style scoped lang="scss">
.page-content {
    min-height: calc(100vh - 20px);
}
.payload-pre {
    background: #0b1220;
    color: #e5e7eb;
    padding: 12px;
    border-radius: 8px;
    max-height: 320px;
    overflow: auto;
    font-size: 12px;
    line-height: 1.4;
}
</style>

